/**
 * @file ProductSkuRow.tsx
 * @description Строка фасовки в боковой колонке карточки товара (phase19.31,
 * разбор B: «фасовки — компактный список»; заменила плитку `ProductSkuCard`).
 *
 * Первая линия — название фасовки (нет своего — вес: «50 г») и цена; вторая —
 * артикул (моноширинным, это код, SC43) · вес или объём · остаток. «⋯» справа: изменить, печать
 * этикеток (если есть права склада), удалить — или «Вернуть в оборот» у
 * архивной. Архивная строка приглушена и подписана «в архиве». Нет данных
 * для «Честного знака» — янтарная подпись словами (вместо «ЧЗ?»).
 *
 * Цена `null` — роль её не видит (SC51): «—», а не ноль. Цена и остаток
 * правятся в окне фасовки (`SkuEditDialog`), а не полями в строке.
 */

import { Pencil, Printer, RotateCcw, Trash2 } from "lucide-react";

import { RowActionsMenu, type RowAction } from "../../../../components/ui/RowActionsMenu";
import type { CatalogSku, CatalogUnit } from "../../../../lib/api-catalog";
import { plainQty, skuPriceText } from "../../../../lib/catalog-format";
import { formatQtyValue } from "../../../../lib/picking-format";
import { qtyUnitLabel } from "../../../../lib/qty-input";
import { cn } from "@/lib/utils";

interface Props {
  sku: CatalogSku;
  unit: CatalogUnit | undefined;
  /** Категория подлежит «Честному знаку» — без GTIN и ТН ВЭД фасовка неполна. */
  markingRequired: boolean;
  onEdit: () => void;
  onDelete: () => void;
  onRestore: () => void;
  /** Печать этикеток; не передан — нет прав склада, пункта нет. */
  onPrint?: () => void;
}

/** Пункты «⋯» — данными: у архивной фасовки только «Вернуть в оборот». */
function skuActions({ sku, onEdit, onDelete, onRestore, onPrint }: Props): RowAction[] {
  if (!sku.isActive) return [{ key: "restore", label: "Вернуть в оборот", icon: RotateCcw, run: onRestore }];
  const actions: RowAction[] = [{ key: "edit", label: "Изменить", icon: Pencil, run: onEdit }];
  // Будущее (phase19.21, SC50): вкладка «Тех.карта» живёт в окне фасовки — пункт «Изменить» открывает его.
  if (onPrint) actions.push({ key: "print", label: "Печать этикеток", icon: Printer, run: onPrint });
  actions.push({ key: "delete", label: "Удалить", icon: Trash2, danger: true, run: onDelete });
  return actions;
}

export function ProductSkuRow(props: Props) {
  const { sku, unit, markingRequired } = props;
  const archived = !sku.isActive;
  const markingMissing = markingRequired && !archived && !(sku.gtin && sku.tnved);
  // «50 г», а не «50.000 g»: число без хвоста, единица из реестра (TD-53).
  const amount = `${formatQtyValue(sku.saleQuantity)} ${unit ? qtyUnitLabel(unit.code) : ""}`.trim();
  // Без своего названия фасовку узнают по весу: «50 г», а не артикул дважды.
  const name = sku.displayName?.trim() || amount || sku.code;
  const second = [amount === name ? null : amount, `остаток ${plainQty(sku.stockQuantity)} шт`, archived ? "в архиве" : null].filter(Boolean).join(" · ");

  return (
    <li className={cn("flex min-h-16 items-center gap-2 py-2", archived && "opacity-70")}>
      <div className="flex min-w-0 flex-1 flex-col gap-0.5">
        <div className="flex min-w-0 items-baseline justify-between gap-2">
          <span className="type-body min-w-0 truncate font-medium text-on-surface">{name}</span>
          <span className="type-body shrink-0 font-semibold tabular-nums text-on-surface">{skuPriceText(sku)}</span>
        </div>
        <p className="type-caption min-w-0 truncate text-on-surface-variant">
          <span className="font-mono">{sku.code}</span>
          {` · ${second}`}
        </p>
        {markingMissing ? (
          <p className="type-caption text-[var(--status-attention-fg,var(--warning))]">Нет GTIN или ТН ВЭД для «Честного знака»</p>
        ) : null}
      </div>
      <RowActionsMenu actions={skuActions(props)} size="compact" title={`Фасовка ${name}`} ariaLabel={`Действия: ${name}`} />
    </li>
  );
}
